import { RunContextApp } from "framework/RCApp";
import { 
  GetClusters,
  GetClustersList,
  GetClusterAttributes,
  CreateClusterAttributes,
  GetClusterConfigs,
  CreateCluster,
  GetClusterDetails,
  GetFeatureMetric,
  UpdateCluster,
  SaveFeatureAccess,
  GetFeatureMetricSchema
} from "types/apis/clusterInterfaces";
import { IngestionConfigsResponse } from "types/apis/pilotInterfaces";

export interface ClusterApiResponse<T> {
  success: boolean;
  data?: T;
  error?: any;
}

export const ClusterApis = {
  /**
   * Get clusters for a project
   */
  getClusters: async (
    rc: RunContextApp, 
    projectId: string
  ): Promise<ClusterApiResponse<GetClusters.retval>> => {
    try {
      const response = await rc.bffClient.getClusters(projectId);
      
      // Check if response has error property (error case)
      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }
      
      // Success case - response is AxiosResponse
      if ('data' in response) {
        return {
          success: true,
          data: response.data
        };
      }
      
      return {
        success: false,
        error: 'Unexpected response format'
      };
    } catch (error) {
      rc.error(rc.getName(ClusterApis), 
        `Error >> GET request to : getClusters for project ${projectId}\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  /**
   * Get cluster attributes for a project
   */
  getAttributes: async (
    rc: RunContextApp,
    projectId: string
  ): Promise<ClusterApiResponse<GetClusterAttributes.retval>> => {
    try {
      const response = await rc.bffClient.getAttributes(projectId);
      
      // Check if response has error property (error case)
      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }
      
      // Success case - response is AxiosResponse
      const responseData = ('data' in response && response.data) ? response.data : null;
      const attributes = Array.isArray(responseData) 
        ? responseData 
        : (responseData?.data || []);
      
      return {
        success: true,
        data: attributes
      };
    } catch (error) {
      rc.error(rc.getName(ClusterApis), 
        `Error >> GET request to : getAttributes for project ${projectId}\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  /**
   * Create cluster attributes
   */
  createAttributes: async (
    rc: RunContextApp,
    requestData: CreateClusterAttributes.params
  ): Promise<ClusterApiResponse<CreateClusterAttributes.retval>> => {
    try {
      const response = await rc.bffClient.createAttributes(requestData);
      
      // Check if response has error property (error case)
      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }
      
      // Success case - response is AxiosResponse
      const responseData = ('data' in response && response.data) ? response.data : null;
      
      return {
        success: true,
        data: responseData
      };
    } catch (error) {
      rc.error(rc.getName(ClusterApis), 
        `Error >> POST request to : createAttributes\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  /**
   * Get cluster configs
   */
  getClusterConfigs: async (
    rc: RunContextApp,
    clusterId: string,
    projectId: string
  ): Promise<IngestionConfigsResponse> => {
    try {
      const response = await rc.bffClient.getClusterConfigs(clusterId, projectId);
      
      // Check if response has error property (from catch block)
      if ('error' in response && response.error) {
        return {
          success: false,
          data: [],
          error: response.error
        };
      }
      
      // Handle axios response - the API returns array directly
      if ('data' in response && response.data) {
        // The API returns the array directly, not wrapped in a data property
        const configData = Array.isArray(response.data) ? response.data : response.data.data || [];
        return {
          success: true,
          data: configData
        };
      }
      
      return {
        success: true,
        data: []
      };
    } catch (error) {
      rc.error(rc.getName(ClusterApis), 
        `Error >> GET request to : getClusterConfigs for cluster ${clusterId}\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  /**
   * Create a new cluster
   */
  createCluster: async (
    rc: RunContextApp,
    requestData: CreateCluster.params
  ): Promise<ClusterApiResponse<CreateCluster.retval>> => {
    try {
      const response = await rc.bffClient.createCluster(requestData);
      
      // Check if response has error property (error case)
      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }
      
      // Success case - response is AxiosResponse
      if ('data' in response) {
        return {
          success: true,
          data: response.data
        };
      }
      
      return {
        success: false,
        error: 'Unexpected response format'
      };
    } catch (error) {
      rc.error(rc.getName(ClusterApis), 
        `Error >> POST request to : createCluster\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  /**
   * Get clusters list (new endpoint)
   * @param cluster_categories - Optional category filter (e.g., 'FEATURE', 'PROGRAM', 'TEMPLATE', 'CONTENT')
   */
  getClustersList: async (
    rc: RunContextApp,
    projectId: string,
    cluster_categories?: string
  ): Promise<ClusterApiResponse<GetClustersList.retval>> => {
    try {
      const response = await rc.bffClient.getClustersList(projectId, cluster_categories);
      
      // Check if response has error property (error case)
      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }
      
      // Success case - response is AxiosResponse
      // The API returns { success: true, data: [...] } directly in response.data
      if ('data' in response && response.data) {
        const responseData = response.data;
        
        // Handle the response structure: { success: true, data: [...] }
        if (responseData.success && Array.isArray(responseData.data)) {
          return {
            success: true,
            data: responseData.data
          };
        }
        
        // Fallback: if data is directly an array
        if (Array.isArray(responseData)) {
          return {
            success: true,
            data: responseData
          };
        }
      }
      
      return {
        success: false,
        error: 'Unexpected response format'
      };
    } catch (error) {
      rc.error(rc.getName(ClusterApis), 
        `Error >> GET request to : getClustersList\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  /**
   * Get cluster details by project ID and cluster ID
   */
  getClusterDetails: async (
    rc: RunContextApp,
    projectId: string,
    clusterId: string
  ): Promise<ClusterApiResponse<GetClusterDetails.retval>> => {
    try {
      const response = await rc.bffClient.getClusterDetails(projectId, clusterId);
      
      // Check if response has error property (error case)
      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }
      
      // Success case - response is AxiosResponse
      if ('data' in response && response.data) {
        const responseData = response.data;
        
        // Handle the response structure: { success: true, data: { data: { payload: {...} } } }
        if (responseData.success && responseData.data) {
          return {
            success: true,
            data: responseData
          };
        }
        
        // Fallback: if data is directly an object
        if (typeof responseData === 'object' && !Array.isArray(responseData)) {
          return {
            success: true,
            data: responseData
          };
        }
      }
      
      return {
        success: false,
        error: 'Unexpected response format'
      };
    } catch (error) {
      rc.error(rc.getName(ClusterApis), 
        `Error >> GET request to : getClusterDetails for project ${projectId} and cluster ${clusterId}\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  /**
   * Get feature metric data for a pilot
   */
  getFeatureMetric: async (
    rc: RunContextApp,
    pilotId: string,
    level: string = 'LEVEL_2'
  ): Promise<ClusterApiResponse<GetFeatureMetric.retval>> => {
    try {
      const response = await rc.bffClient.getFeatureMetric(pilotId, level);
      
      // Check if response has error property (error case)
      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }
      
      // Success case - response is AxiosResponse
      if ('data' in response && response.data) {
        const responseData = response.data;
        
        // Handle nested success/data structure
        if (responseData.success && responseData.data) {
          const innerData = responseData.data;
          
          // Handle new response format with featureResolutions
          if (innerData.featureResolutions) {
            return {
              success: true,
              data: {
                features: innerData.featureResolutions,
                clusterPriority: innerData.clusterPriority
              }
            };
          }
          
          return {
            success: true,
            data: innerData
          };
        }
        
        // Handle direct featureResolutions response
        if (responseData.featureResolutions) {
          return {
            success: true,
            data: {
              features: responseData.featureResolutions,
              clusterPriority: responseData.clusterPriority
            }
          };
        }
        
        // Handle direct data response
        if (Array.isArray(responseData)) {
          return {
            success: true,
            data: { features: responseData }
          };
        }
        
        // Fallback: return responseData as is
        return {
          success: true,
          data: responseData
        };
      }
      
      return {
        success: false,
        error: 'Unexpected response format'
      };
    } catch (error) {
      rc.error(rc.getName(ClusterApis), 
        `Error >> GET request to : getFeatureMetric for pilot ${pilotId}\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  /**
   * Update cluster details (for Virtual clusters)
   */
  updateCluster: async (
    rc: RunContextApp,
    clusterId: string,
    requestData: {
      cluster_categories: string[];
      cluster_name: string;
      evaluation_criteria: string;
      active: boolean;
    }
  ): Promise<ClusterApiResponse<UpdateCluster.retval>> => {
    try {
      const response = await rc.bffClient.updateCluster(clusterId, requestData);
      
      // Check if response has error property (error case)
      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }
      
      // Success case - response is AxiosResponse
      if ('data' in response) {
        return {
          success: true,
          data: response.data
        };
      }
      
      return {
        success: false,
        error: 'Unexpected response format'
      };
    } catch (error) {
      rc.error(rc.getName(ClusterApis), 
        `Error >> PUT request to : updateCluster for cluster ${clusterId}\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  /**
   * Save feature access configuration to S3 and notify backend
   */
  saveFeatureAccess: async (
    rc: RunContextApp,
    requestData: SaveFeatureAccess.params
  ): Promise<ClusterApiResponse<SaveFeatureAccess.retval>> => {
    try {
      const response = await rc.bffClient.saveFeatureAccess(requestData);
      
      // Check if response has error property (error case)
      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }
      
      // Success case - response is AxiosResponse
      if ('data' in response && response.data) {
        const responseData = response.data;
        
        return {
          success: responseData.success ?? true,
          data: responseData
        };
      }
      
      return {
        success: false,
        error: 'Unexpected response format'
      };
    } catch (error) {
      rc.error(rc.getName(ClusterApis), 
        `Error >> POST request to : saveFeatureAccess\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  /**
   * Get feature metric schema for a project
   * Returns email level2 FCMs, sections, events, and other schema data
   */
  getFeatureMetricSchema: async (
    rc: RunContextApp,
    projectId: string
  ): Promise<ClusterApiResponse<GetFeatureMetricSchema.retval>> => {
    try {
      const response = await rc.bffClient.getFeatureMetricSchema(projectId);
      
      // Check if response has error property (error case)
      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }
      
      // Success case - response is AxiosResponse
      if ('data' in response && response.data) {
        const responseData = response.data;
        
        // Handle nested success/data structure
        if (responseData.success && responseData.data) {
          return {
            success: true,
            data: responseData.data
          };
        }
        
        // Handle direct data response (email, her, dashboard at top level)
        if (responseData.email || responseData.her || responseData.dashboard) {
          return {
            success: true,
            data: responseData
          };
        }
        
        // Fallback: return responseData as is
        return {
          success: true,
          data: responseData
        };
      }
      
      return {
        success: false,
        error: 'Unexpected response format'
      };
    } catch (error) {
      rc.error(rc.getName(ClusterApis), 
        `Error >> GET request to : getFeatureMetricSchema for project ${projectId}\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  },

  /**
   * Push new sections for email templates
   */
  pushNewSections: async (
    rc: RunContextApp,
    payload: {
      project_id: string;
      email: { event: string; sections: string[] }[];
    }
  ): Promise<ClusterApiResponse<any>> => {
    try {
      const response = await rc.bffClient.pushNewSections(payload);
      
      // Check if response has error property (error case)
      if ('error' in response && response.error) {
        return {
          success: false,
          error: response.error
        };
      }
      
      // Success case - response is AxiosResponse
      if ('data' in response) {
        return {
          success: true,
          data: response.data
        };
      }
      
      return {
        success: false,
        error: 'Unexpected response format'
      };
    } catch (error) {
      rc.error(rc.getName(ClusterApis), 
        `Error >> POST request to : pushNewSections\n` + 
        `\nresulted in error : \n`, error);
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }
};

